import { faUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Flex, Typography } from 'antd';
import { Link } from 'react-router-dom';

import {
  BANK,
  CASH,
  U_ARTICLE,
  U_CLIENT,
  U_COMMENT,
  U_DETAILS,
  U_KEY,
  U_OPERATIONS,
  U_SUM,
  U_USER,
} from '@app/constants';
import type { NClientPayments } from '@app/types/ClientPayments';
import { divideAmount, formatISODateToCustomFormat } from '@app/utils';

const { Text, Paragraph } = Typography;

interface Props {
  payments: NClientPayments.ModelPaymentExpenseInfo[];
  handleFileClick(id: number): Promise<void>;
  handleFileOpen(id: number): Promise<void>;
  handlePaymentInfoModalOpen: (
    objectId: number | null,
    isExpense: boolean
  ) => void;
}
export const adaptDataToTable = ({
  payments,
  handleFileClick,
  handleFileOpen,
  handlePaymentInfoModalOpen,
}: Props) => {
  return payments.map((payment: NClientPayments.ModelPaymentExpenseInfo) => {
    const {
      ObjectId,
      IsPayment,
      IsCollection,
      Amount,
      Client,
      User,
      PayType,
      Comment,
      ClientId,
      ExpenseArticleId,
      ExpenseArticle,
      ClientRecords,
      PaymentAccount,
      LegalEntity,
      Timestamp,
      UserId,
      Attachments,
    } = payment;

    const isExpense =
      !IsPayment && !IsCollection && !ClientId && ExpenseArticleId !== 0;

    const getOperationName = () => {
      if (isExpense) {
        return 'Расход';
      }

      if (!IsPayment && IsCollection) {
        return 'Инкассация';
      }

      if (
        IsPayment &&
        !IsCollection &&
        ClientId &&
        ClientRecords &&
        ClientRecords.length > 0
      ) {
        return (
          <div>
            Оплата визита{' '}
            {ClientRecords.map((item, index) => {
              return `${item.Id}${index === ClientRecords.length ? '' : ', '}`;
            })}
          </div>
        );
      }

      if (IsPayment && !IsCollection && !ClientId) {
        return 'Пополнение кассы';
      }

      if (
        IsPayment &&
        !IsCollection &&
        ClientId &&
        ClientRecords?.length === 0 &&
        ClientId > 0 &&
        Amount < 0
      ) {
        return 'Возврат';
      }

      if (
        IsPayment &&
        !IsCollection &&
        ClientId &&
        ClientRecords?.length === 0
      ) {
        return 'Поступление';
      }

      return '';
    };

    const getPayType = () => {
      if (PayType === null) {
        return '';
      } else if (PayType === BANK) {
        return 'Банковской картой';
      } else if (PayType === CASH) {
        return 'Наличными';
      }
    };

    return {
      [U_KEY]: String(`${ObjectId}-${IsPayment}-${IsCollection}`),
      [U_OPERATIONS]: (
        <div>
          {isExpense ? (
            <Text>{getOperationName()}</Text>
          ) : (
            <Button
              color="default"
              type="link"
              className="operation-name-button"
              onClick={() => handlePaymentInfoModalOpen(ObjectId, isExpense)}
              data-testid="name-operation"
            >
              {getOperationName()}
            </Button>
          )}
          <br />
          <Text type="secondary" data-testid="date-time-operation">
            {formatISODateToCustomFormat(Timestamp)}
          </Text>
        </div>
      ),
      [U_SUM]: (
        <div data-testid="sum-operation">
          {!IsPayment && <Text className="expense-amount">-</Text>}
          <Text className={!IsPayment ? 'expense-amount' : ''}>
            {divideAmount(Amount)}
          </Text>
        </div>
      ),
      [U_CLIENT]: (
        <div>
          {ClientId && ClientId !== 0 ? (
            <Link
              to={`/clients/card/?id=${ClientId}`}
              data-testid="surname-client"
            >
              {Client}
            </Link>
          ) : (
            ''
          )}
        </div>
      ),
      [U_USER]: (
        <div>
          <Link
            to={`/users/edit/?id=${UserId}&key=1`}
            data-testid="surname-user"
          >
            {User}
          </Link>
        </div>
      ),
      [U_ARTICLE]:
        getOperationName() === 'Возврат' ? (
          <Text data-testid="article-operation">Возврат с баланса клиента</Text>
        ) : ExpenseArticle ? (
          <Text data-testid="article-operation">{ExpenseArticle}</Text>
        ) : (
          <></>
        ),
      [U_DETAILS]: (
        <div>
          {PayType && (
            <Text strong data-testid="method-payment">
              {getPayType()} <br />
            </Text>
          )}
          <Text type="secondary" data-testid="cash-register-data">
            {PaymentAccount && PaymentAccount}
            {LegalEntity && `, ${LegalEntity}`}
          </Text>{' '}
          <br />
          {!ClientId && <Text type="secondary">{Client}</Text>}
        </div>
      ),
      [U_COMMENT]: (
        <div>
          <Paragraph ellipsis={{ rows: 3 }} data-testid="comment-text">
            {Comment}
          </Paragraph>
          {!!Attachments &&
            Attachments.map((item) => {
              return (
                <Flex key={item.Id} align="center" gap={8}>
                  <Button
                    onClick={() => handleFileClick(item.Id)}
                    type="link"
                    className="download-file-button"
                  >
                    {item.FileName}
                  </Button>
                  <Button
                    icon={<FontAwesomeIcon icon={faUpRightFromSquare} />}
                    onClick={() => handleFileOpen(item.Id)}
                    data-testid="open-file-in-new-window"
                  />
                </Flex>
              );
            })}
        </div>
      ),
    };
  });
};
